import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ProductCard from '../components/ui/ProductCard';
import { products } from '../data/mockData';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const Home = () => {
  return (
    <div className="overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative h-[90vh] min-h-[600px] flex items-center justify-center">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1582555172866-f73bb12a2ab3?auto=format&fit=crop&q=80&w=2000" 
            alt="Artistic Living Space" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-charcoal/30 mix-blend-multiply" />
        </div>
        
        <div className="relative z-10 text-center text-white px-6 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <span className="text-[11px] uppercase tracking-[0.4em] font-bold mb-6 block text-gallery/80">Est. 2024 — Fine Art Editions</span>
            <h1 className="text-5xl md:text-8xl font-serif mb-8 leading-[1.1] tracking-tight">
              Bespoke Frames <br/>
              <span className="italic font-light">for Timeless Art</span>
            </h1>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-12">
              <Link to="/shop" className="w-full sm:w-auto bg-white text-charcoal px-10 py-5 uppercase tracking-widest text-[11px] font-bold hover:bg-ultramarine hover:text-white transition-all duration-300 shadow-xl">
                Shop The Collection
              </Link>
              <Link to="/info" className="w-full sm:w-auto bg-transparent border border-white/40 backdrop-blur-sm text-white px-10 py-5 uppercase tracking-widest text-[11px] font-bold hover:bg-white hover:text-charcoal transition-all duration-300">
                Framing Guide
              </Link>
            </div>
          </motion.div>
        </div>
        
        {/* Scroll Indicator */}
        <motion.div 
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white/60"
        >
          <SafeIcon icon={FiIcons.FiChevronDown} className="text-2xl" />
        </motion.div>
      </section>

      {/* Featured Grid */}
      <section className="py-32 container mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-6">
          <div className="max-w-xl">
            <h2 className="text-4xl md:text-6xl font-serif mb-6 leading-tight">New Arrivals</h2>
            <p className="text-gray-500 leading-relaxed text-lg">Our latest curator-selected pieces, printed on archival cotton rag and custom-framed to your specification.</p>
          </div>
          <Link to="/shop" className="group flex items-center gap-3 text-ultramarine font-bold uppercase tracking-widest text-[11px]">
            Explore All <SafeIcon icon={FiIcons.FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-20">
          {products.slice(0, 3).map((product, index) => (
            <ProductCard key={product.id} product={product} index={index} />
          ))}
        </div>
      </section>

      {/* Room Selection - Horizontal Scroll */}
      <section className="bg-charcoal py-32 overflow-hidden">
        <div className="container mx-auto px-6 md:px-12 mb-16">
          <h2 className="text-white text-4xl md:text-5xl font-serif text-center">Shop by Environment</h2>
        </div>
        
        <div className="flex gap-8 px-6 md:px-12 overflow-x-auto hide-scrollbar pb-10">
          {[
            { name: 'Minimalist Office', image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=800' },
            { name: 'Zen Bedroom', image: 'https://images.unsplash.com/photo-1616594039964-ae9021a400a0?auto=format&fit=crop&q=80&w=800' },
            { name: 'Grand Living', image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&q=80&w=800' },
            { name: 'Modern Kitchen', image: 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&q=80&w=800' }
          ].map((room, idx) => (
            <div key={idx} className="min-w-[300px] md:min-w-[450px] group cursor-pointer">
              <div className="aspect-[4/5] overflow-hidden mb-6 relative">
                <img src={room.image} alt={room.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <h3 className="text-white font-serif text-2xl md:text-3xl border-b border-white/30 pb-2">{room.name}</h3>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Values Section */}
      <section className="py-32 container mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
          <div className="text-center">
            <SafeIcon icon={FiIcons.FiShield} className="text-3xl text-ultramarine mx-auto mb-6" />
            <h4 className="font-serif text-2xl mb-4">Museum Quality</h4>
            <p className="text-gray-500 text-sm leading-relaxed">Archival inks and 310gsm cotton rag paper ensure your print stays vibrant for over 100 years.</p>
          </div>
          <div className="text-center">
            <SafeIcon icon={FiIcons.FiWind} className="text-3xl text-ultramarine mx-auto mb-6" />
            <h4 className="font-serif text-2xl mb-4">Sustainable Wood</h4>
            <p className="text-gray-500 text-sm leading-relaxed">Every frame is custom-milled from FSC-certified ash and oak, hand-finished by our artisans.</p>
          </div>
          <div className="text-center">
            <SafeIcon icon={FiIcons.FiTruck} className="text-3xl text-ultramarine mx-auto mb-6" />
            <h4 className="font-serif text-2xl mb-4">Secure Shipping</h4>
            <p className="text-gray-500 text-sm leading-relaxed">Double-walled custom crates and reinforced corners protect your investment during transit.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;